import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * Aurora Card — primary surface container.
 *
 * Tones:
 * - `default` — white card raised above the parchment background.
 * - `muted`   — uses the muted token; reads as "embedded in the page".
 * - `outline` — borderless body with a single hairline; sits flat, no shadow.
 *
 * `interactive` toggles a hover lift; use for cards that are clickable
 * (whole-card links, document cards, etc.).
 *
 * Composition: cards are flat by default; use CardHeader / CardContent /
 * CardFooter to apply consistent 20px padding rhythm. The `:not(:last-child)`
 * / `:not(:first-child)` siblings dedupe so a card with only a header still
 * pads correctly, and a header followed by content collapses to a single
 * 20px gap.
 */
const cardVariants = cva(
  'rounded-lg border bg-card text-card-foreground transition-[box-shadow,border-color,transform] duration-150 ease-out',
  {
    variants: {
      tone: {
        default: 'shadow-soft border-border',
        muted: 'bg-muted border-transparent shadow-none',
        outline: 'border-border bg-transparent shadow-none',
      },
      interactive: {
        true: 'hover:shadow-raised hover:border-border-strong/70 hover:-translate-y-px',
        false: '',
      },
    },
    defaultVariants: {
      tone: 'default',
      interactive: false,
    },
  },
)

function Card({
  className,
  tone,
  interactive,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      data-tone={tone ?? 'default'}
      className={cn(cardVariants({ tone, interactive, className }))}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'flex flex-col gap-1 p-5 [&:not(:last-child)]:pb-0',
        'has-data-[slot=card-action]:flex-row has-data-[slot=card-action]:items-start has-data-[slot=card-action]:justify-between has-data-[slot=card-action]:gap-3',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-base font-semibold leading-tight tracking-tight text-foreground',
        className,
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-sm leading-relaxed text-muted-foreground', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('p-5', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex items-center gap-2 p-5 [&:not(:first-child)]:pt-0',
        className,
      )}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('flex shrink-0 items-center gap-2', className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
}
